import React from 'react';
import { useSelector } from 'react-redux';
import { useLabels, usePrefs } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';
import { transformVehiclesAttrs } from '../utilities/transformVehiclesAttrs';
import ColorSwatch from './ColorSwatch';
import StickySide from './common/StickySide';

const VehiclesAttributeRow = () => {
	const vehicles = useSelector((state) => state.vehicles);
	const labels = useLabels();
	const { showOdometerOnNew } = usePrefs();

	const compareAttrs = [
		'LOCATION',
		'CONDITION',
		'MILEAGE',
		'FUEL_ECONOMY',
		'EXTERIOR_COLOR',
		'INTERIOR_COLOR',
		'DRIVELINE',
		'ENGINE',
		'BODY_SEATING',
		'TRANSMISSION'
	];
	const compareValuesMap = transformVehiclesAttrs(
		compareAttrs,
		vehicles,
		labels,
		{ showOdometerOnNew }
	);

	// checking if location is the same for all vehicles
	if (new Set(compareValuesMap.get('LOCATION')).size === 1) {
		compareValuesMap.delete('LOCATION');
	}

	// checking if condition is the same for all vehicles
	if (new Set(compareValuesMap.get('CONDITION')).size === 1) {
		compareValuesMap.delete('CONDITION');
	}

	function renderLabel(label) {
		let name;
		if (label === 'BODY_SEATING') {
			name = `${labels.get('BODY')} / ${labels.get('SEATING')}`;
		} else if (label === 'CONDITION') {
			name = labels.get('COMPARE_CONDITION');
		} else {
			name = labels.get(label);
		}
		return <div className="font-weight-bold">{name}</div>;
	}

	function renderValue(attribute, value) {
		if (attribute === 'EXTERIOR_COLOR' || attribute === 'INTERIOR_COLOR') {
			return <ColorSwatch color={value.color} text={value.text} />;
		}
		return <div>{value}</div>;
	}

	const cellContentClasses = [
		'ddc-font-size-small',
		'd-sm-flex',
		'flex-sm-column',
		'align-items-left',
		'ml-3'
	];

	return [...compareValuesMap.keys()].flatMap((attribute, index) => [
		<tr key={`header-${attribute}`}>
			<th
				className={setClassNames([
					'col-1',
					'border-top-0',
					'pb-0',
					index !== 0 && 'pt-1'
				])}
				colSpan={`${vehicles.length}`}
				id={attribute}
				scope={index === 0 ? 'colgroup' : undefined}
			>
				{index !== 0 && <hr className="divider" />}
				<StickySide
					className={setClassNames([
						...cellContentClasses,
						'mb-0',
						index === 0 && 'mt-3'
					])}
				>
					{renderLabel(attribute)}
				</StickySide>
			</th>
		</tr>,
		<tr
			data-testid={`vehicles-attribute-${attribute}-row`}
			key={`row-${attribute}`}
		>
			{compareValuesMap.get(attribute)?.map((value, vehicleIndex) => {
				const { uuid } = vehicles[vehicleIndex];
				return (
					<td
						className={`col-${
							vehicleIndex + 1
						} pt-0 pb-0 border-top-0`}
						headers={`${attribute} ${uuid}`}
						key={`${attribute}-${uuid}`}
					>
						<div
							className={setClassNames([
								...cellContentClasses,
								'mr-3',
								'mb-3',
								'mt-2'
							])}
						>
							{renderValue(attribute, value)}
						</div>
					</td>
				);
			})}
		</tr>
	]);
};

export default VehiclesAttributeRow;
